(function() {
  'use strict';

  // =============================================================================
  // CONFIGURATION
  // =============================================================================

  var CONFIG = {
    paramName: 'discount',
    cookieName: 'pawmate_discount',
    expirySeconds: 10800, // 3 hours in seconds
    // Accept any alphanumeric code with optional dashes/underscores (3-50 chars)
    // Covers: PM5HCY7X, WELCOME-ABC123, AC_123456_1738339200, etc.
    codePattern: /^[A-Z0-9_-]{3,50}$/i,
    debug: false
  };

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================

  function log(message, data) {
    if (CONFIG.debug) {
      console.log('[Pawmate Discount]', message, data !== undefined ? data : '');
    }
  }

  function isValidCode(code) {
    if (!code || typeof code !== 'string') return false;
    return CONFIG.codePattern.test(code.toUpperCase());
  }

  // =============================================================================
  // COOKIE FUNCTIONS
  // =============================================================================

  function setCookie(name, value, maxAgeSeconds) {
    var cookie = name + '=' + encodeURIComponent(value);
    cookie += '; max-age=' + maxAgeSeconds;
    cookie += '; path=/';
    cookie += '; SameSite=Lax';
    document.cookie = cookie;
    log('Cookie set:', name + '=' + value + ' (expires in ' + (maxAgeSeconds / 3600) + 'h)');
  }

  function getCookie(name) {
    var nameEQ = name + '=';
    var cookies = document.cookie.split(';');
    for (var i = 0; i < cookies.length; i++) {
      var c = cookies[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  }

  function removeCookie(name) {
    // Set cookie with past expiry to delete it
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
    log('Cookie removed:', name);
  }

  // =============================================================================
  // DISCOUNT CODE FUNCTIONS
  // =============================================================================

  /**
   * Parse discount code from URL
   * @returns {string|null} - Discount code or null
   */
  function parseDiscountFromURL() {
    try {
      var urlParams = new URLSearchParams(window.location.search);
      var code = urlParams.get(CONFIG.paramName);

      if (code && isValidCode(code)) {
        log('Parsed from URL:', code.toUpperCase());
        return code.toUpperCase();
      }
    } catch (e) {
      // Fallback for older browsers
      var regex = new RegExp('[?&]' + CONFIG.paramName + '=([^&#]*)');
      var results = regex.exec(window.location.search);
      if (results && results[1]) {
        var code = decodeURIComponent(results[1]);
        if (isValidCode(code)) {
          log('Parsed from URL (fallback):', code.toUpperCase());
          return code.toUpperCase();
        }
      }
    }
    return null;
  }

  /**
   * Store discount code in cookie (auto-expires after 3h)
   * Also applies to Shopify session so discount shows in cart.
   * Checkout button will clear session and pass via URL for X button to work.
   * @param {string} code - Discount code to store
   * @param {boolean} skipValidation - Skip API validation (for already validated codes)
   */
  function storeDiscountCode(code, skipValidation) {
    if (!isValidCode(code)) {
      log('Invalid code, not storing:', code);
      return;
    }

    var sanitizedCode = code.toUpperCase().substring(0, 20);

    // Set cookie with 3h expiry - browser auto-deletes after expiry
    setCookie(CONFIG.cookieName, sanitizedCode, CONFIG.expirySeconds);

    // Apply discount to Shopify session so it shows in cart
    // The checkout button will clear this session before redirecting
    fetch('/discount/' + encodeURIComponent(sanitizedCode), {
      method: 'GET',
      credentials: 'same-origin'
    }).then(function() {
      log('Discount applied to Shopify session:', sanitizedCode);
    }).catch(function(err) {
      log('Failed to apply discount to Shopify:', err.message);
    });

    log('Discount code stored in cookie (3h expiry):', sanitizedCode);

    // Dispatch custom event for other scripts (e.g., cart-drawer)
    try {
      window.dispatchEvent(new CustomEvent('pawmate:discount:stored', {
        detail: { code: sanitizedCode }
      }));
    } catch (e) {
      log('Event dispatch failed:', e.message);
    }
  }

  /**
   * Get customer email from logged in user or checkout cookies
   * @returns {string|null}
   */
  function getCustomerEmail() {
    // First try logged in user
    var email = ((window.PawmateSnippetCfg||{}).customerEmail||'') || null;
    if (email) return email;

    // Check checkout cookies for email
    try {
      var cookies = document.cookie.split(';');
      for (var i = 0; i < cookies.length; i++) {
        var parts = cookies[i].trim().split('=');
        var name = parts[0];
        var value = parts[1] || '';
        // Shopify stores checkout email in various cookies
        if (name === 'checkout_email' || name === '_shopify_y') {
          var decoded = decodeURIComponent(value);
          if (decoded && decoded.indexOf('@') !== -1) {
            return decoded;
          }
        }
      }
    } catch (e) {}

    return null;
  }

  /**
   * Validate discount code via API before storing
   * Checks if the code is valid and if it's assigned to the current customer
   * @param {string} code - Discount code to validate
   * @returns {Promise<{valid: boolean, error?: string}>}
   */
  async function validateAndStoreDiscountCode(code) {
    if (!isValidCode(code)) {
      log('Invalid code format, not validating:', code);
      return { valid: false, error: 'Nieprawidłowy format kodu' };
    }

    var sanitizedCode = code.toUpperCase().substring(0, 20);

    // Get current customer email (logged in or from checkout cookies)
    var customerEmail = getCustomerEmail();

    try {
      var response = await fetch('/apps/pawmate/validate-discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: sanitizedCode,
          customerEmail: customerEmail
        }),
        credentials: 'same-origin'
      });

      var data = await response.json();

      if (data.valid) {
        // Code is valid and customer is allowed - store it
        storeDiscountCode(sanitizedCode, true);
        return { valid: true, discount: data.discount };
      } else if (data.customerMismatch) {
        // Customer email doesn't match - DON'T store, show error
        log('Code validation failed - customer mismatch');
        showValidationError('Ten kod rabatowy jest przypisany do innego konta.');
        return { valid: false, error: data.error };
      } else {
        // Other validation error (code not found, expired, etc.)
        // FALLBACK: Store anyway, let Shopify validate at checkout
        log('Code validation failed, using fallback:', data.error);
        storeDiscountCode(sanitizedCode, true);
        return { valid: true, discount: null };
      }
    } catch (err) {
      log('Validation request failed, using fallback:', err.message);
      // On network error, store the code anyway (let Shopify validate at checkout)
      storeDiscountCode(sanitizedCode, true);
      return { valid: true, discount: null };
    }
  }

  /**
   * Show validation error banner
   * @param {string} message - Error message to display
   */
  function showValidationError(message) {
    var banner = document.getElementById('pawmate-discount-banner');
    var codeEl = document.getElementById('pawmate-discount-banner-code');

    if (!banner) return;

    // Change banner style to error
    banner.style.background = 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)';
    banner.style.boxShadow = '0 8px 32px rgba(220, 38, 38, 0.3)';

    // Update text
    var textEl = banner.querySelector('.pawmate-discount-banner__text');
    if (textEl) {
      textEl.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:6px;vertical-align:middle;"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>' + message;
    }

    // Show banner
    banner.classList.add('is-visible');

    // Auto-hide after 5 seconds
    setTimeout(function() {
      banner.classList.remove('is-visible');
    }, 5000);
  }

  /**
   * Retrieve discount code from cookie
   * @returns {string|null} - Discount code or null
   */
  function getDiscountCode() {
    var code = getCookie(CONFIG.cookieName);

    if (code && isValidCode(code)) {
      log('Retrieved from cookie:', code);
      return code;
    }

    return null;
  }

  /**
   * Clear discount code from cookie
   */
  function clearDiscountCode() {
    removeCookie(CONFIG.cookieName);

    // Also try clearing Shopify's discount_code cookie (belt and suspenders)
    var domains = ['', '.pawmate.pl', 'pawmate.pl'];
    var paths = ['/', ''];
    domains.forEach(function(domain) {
      paths.forEach(function(path) {
        document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=' + path + (domain ? '; domain=' + domain : '');
      });
    });

    log('Discount code cleared from cookie');

    try {
      window.dispatchEvent(new CustomEvent('pawmate:discount:cleared'));
    } catch (e) {
      log('Event dispatch failed:', e.message);
    }
  }

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  async function init() {
    log('Initializing discount tracking system (cookie-based, 3h expiry)');

    // Step 1: Parse discount code from URL
    var urlCode = parseDiscountFromURL();

    if (urlCode) {
      // New discount code from URL - VALIDATE before storing
      log('Validating discount code from URL:', urlCode);

      var result = await validateAndStoreDiscountCode(urlCode);

      if (result.valid) {
        // Track for analytics (with consent check)
        if (window.pawmateConsent && window.pawmateConsent.hasAnalytics() && typeof gtag === 'function') {
          gtag('event', 'newsletter_discount_landed', {
            discount_code: urlCode,
            landing_page: window.location.pathname
          });
        }
      } else {
        // Track failed validation (with consent check)
        if (window.pawmateConsent && window.pawmateConsent.hasAnalytics() && typeof gtag === 'function') {
          gtag('event', 'newsletter_discount_invalid', {
            discount_code: urlCode,
            error: result.error,
            landing_page: window.location.pathname
          });
        }
      }
    }

    // Step 2: Check for existing discount code
    var existingCode = getDiscountCode();
    if (existingCode) {
      log('Active discount code:', existingCode);
    }

    log('Discount tracking initialized');
  }

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  window.PawmateDiscount = {
    getCode: getDiscountCode,
    clear: clearDiscountCode,
    isValidCode: isValidCode,
    enableDebug: function() {
      CONFIG.debug = true;
      console.log('[Pawmate Discount] Debug mode enabled');
    },
    // Note: With cookies, we can't easily get remaining time
    // Cookie just auto-expires after 3h
    getInfo: function() {
      var code = getDiscountCode();
      if (!code) return null;
      return {
        code: code,
        expiresIn: '~3 hours from when code was saved'
      };
    }
  };

  // =============================================================================
  // RUN INITIALIZATION
  // =============================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
